/**
 * อ่านเมนูจากแชท — pure rules (no Vue).
 * - Spotting a menu in customer text, locally (no AI cost): ≥ 2 catalog menus / P codes, or 1 with a quantity
 *   ("กะเพรา x3"). One action per run of consecutive customer texts, on its last bubble.
 * - A customer picture: the picture + the short menu-ish texts the customer sent right after it (context for the AI).
 * - The recheck list (the POS sale page "รีเช็ค" preview, sale-ai-assist.js showMenuPreview): −/+/type, remove, add
 *   (name or P code), sort P code ↔ customer order, live count vs the pack. Snacks never count towards a pack
 *   (lib/order.ts counts()).
 * - Round 2 (owner 28 ก.ย. 12:35): English names (chat_v1 `name_en`) in the matcher, the search and the pointing;
 *   "สุ่มเมนูที่เหลือ N กล่อง" (the POS sale page #btn-random, sale.js randomProduct()) limited by type chips that the
 *   read's `mix_hint` ("คละเอาแต่ไก่", "ไม่เอาหมู") pre-selects. Staff press it; nothing is filled by itself.
 */
import type { MenuReadItem, MenuReadPack, MenuReadResult, MixHint } from '../api/menuread'
import type { PosCartItem, PosMenu, PosSetMenu } from '../api/pos/types'
import type { ChatMessage, ThreadItem } from '../api/types'

export const MAX_QTY = 200
const RUN_GAP_MS = 30 * 60_000
const RUN_MAX = 10
const AFTER_IMAGE_MS = 10 * 60_000
const AFTER_IMAGE_MAX = 3

// ---------------------------------------------------------------- names and codes

/** "P12.กะเพราไก่" → "P12" (also S4., KT3. like the POS). */
export function pCodeOf(name: string): string | null {
  return /^([A-Z]{1,3}\d+)\./.exec(name ?? '')?.[1] ?? null
}

/** The name without its code: "P12.กะเพราไก่" → "กะเพราไก่". */
export function dishName(name: string): string {
  return (name ?? '').replace(/^[A-Z]{1,3}\d+\.\s*/, '')
}

/** Sort key of the POS preview: P number first (P1 → Pn), everything else after. */
export function pNum(name: string): number {
  const m = /^P(\d+)\./.exec(name ?? '')
  return m ? Number(m[1]) : 99999
}

const norm = (s: string): string =>
  (s ?? '')
    .toLowerCase()
    .replace(/\(.*?\)/g, '')
    .replace(/[\s.\-_:,·•]/g, '')

// ---------------------------------------------------------------- spotting a menu in text (local, no AI)

const QTY_RES = [
  /(?:x|×|\*)\s*\d{1,3}/i,
  /\d{1,3}\s*(?:กล่อง|ถุง|แพ็ค|แพ็ก|ที่|ชุด|อัน|box)/i,
  /(?:^|\s)\d{1,3}\s*(?:ค่ะ|คะ|ครับ|นะ|จ้า)?\s*$/,
  /(?:หนึ่ง|สอง|สาม|สี่|ห้า|หก|เจ็ด|แปด|เก้า|สิบ)\s*(?:กล่อง|ถุง|แพ็ค|แพ็ก|ที่|ชุด)/,
  /อย่างละ\s*\d/,
]
const CODE_IN_TEXT = /(?<![a-z0-9])([a-z]{1,3})\s?(\d{1,3})(?![0-9])/gi
const WINDOW = 6

export interface Detected {
  /** Distinct places in the text that name a menu. */
  mentions: number
  /** A line that names a menu also has a quantity. */
  withQty: boolean
  menuIds: number[]
}

interface Span {
  line: number
  from: number
  to: number
  /** Dishes named here, the longest match first. */
  ids: { id: number; len: number }[]
}

/** Where menus are named in `text` (P codes, or ≥ 6 characters of a dish name in a row). */
export function detectMenus(text: string | null | undefined, menus: PosMenu[]): Detected {
  const out: Detected = { mentions: 0, withQty: false, menuIds: [] }
  if (!text || !menus.length) return out
  const codes = new Map<string, number>()
  for (const m of menus) {
    const c = pCodeOf(m.name)
    if (c) codes.set(c, m.id)
  }
  const spans: Span[] = []
  const lines = text.split(/\n+/)
  lines.forEach((line, li) => {
    const found: Span[] = []
    for (const m of line.matchAll(CODE_IN_TEXT)) {
      const id = codes.get(`${m[1]}${Number(m[2])}`.toUpperCase())
      if (id !== undefined) found.push({ line: li, from: -1 - found.length, to: -1 - found.length, ids: [{ id, len: 99 }] })
    }
    const ln = norm(line)
    for (const m of menus) {
      const hit = bestMatch(norm(dishName(m.name)), ln)
      if (hit) found.push({ line: li, from: hit.at, to: hit.at + hit.len, ids: [{ id: m.id, len: hit.len }] })
      // English names: places counted in the raw line (their own range: they never merge with a Thai place)
      const en = m.name_en ? englishHit(m.name_en, line) : null
      if (en) found.push({ line: li, from: EN_AT + en.from, to: EN_AT + en.to, ids: [{ id: m.id, len: en.len }] })
    }
    // the same place matched by several dishes ("พริกไทย" is in two names) = one mention, the longest match first
    for (const s of found) {
      const same = spans.find((x) => x.line === s.line && x.from >= 0 && s.from >= 0 && s.from < x.to && x.from < s.to)
      if (same) {
        same.ids.push(...s.ids)
        same.ids.sort((a, b) => b.len - a.len)
        same.from = Math.min(same.from, s.from)
        same.to = Math.max(same.to, s.to)
      } else spans.push(s)
    }
    if (found.length && QTY_RES.some((r) => r.test(line))) out.withQty = true
  })
  out.mentions = spans.length
  out.menuIds = [...new Set(spans.flatMap((s) => s.ids.map((x) => x.id)))]
  return out
}

/** The longest piece (≥ 6 characters, or the whole short name) of a dish name found in a line. */
function bestMatch(body: string, ln: string): { at: number; len: number } | null {
  if (body.length < 3) return null
  const w = Math.min(WINDOW, body.length)
  let best: { at: number; len: number } | null = null
  for (let i = 0; i + w <= body.length; i++) {
    if (best && body.length - i <= best.len) break
    let at = ln.indexOf(body.slice(i, i + w))
    if (at < 0) continue
    let len = w
    while (i + len < body.length) {
      const a = ln.indexOf(body.slice(i, i + len + 1))
      if (a < 0) break
      at = a
      len++
    }
    if (!best || len > best.len) best = { at, len }
  }
  return best
}

// ---------------------------------------------------------------- English names (chat_v1 name_en)

const EN_AT = 100_000
/** Words that alone do not tell a dish (meats, cooking words, meal words): they never make a match by themselves. */
const EN_COMMON = new Set(
  (
    'a an and or of in on the with without style set box boxes meal meals menu food rice sauce stir fried fry grilled grill ' +
    'steamed roasted roast baked boiled spicy sweet sour hot soup thai japanese korean chinese chicken pork beef fish ' +
    'seafood shrimp prawn egg eggs mixed minced breast fillet clean healthy please want order only more'
  ).split(' '),
)
const enWords = (s: string): { w: string; at: number; end: number }[] =>
  [...(s ?? '').toLowerCase().matchAll(/[a-z]+/g)].map((m) => ({ w: m[0], at: m.index ?? 0, end: (m.index ?? 0) + m[0].length }))
const enDistinct = (w: string): boolean => w.length >= 4 && !EN_COMMON.has(w)

/**
 * Where an English dish name is in a line (case-insensitive, whole words): the whole name in a row, or ≥ 2 of its
 * words with one that tells the dish ("basil chicken"), or one such word of ≥ 5 letters ("gochujang"). Common words
 * ("chicken", "rice", "with") never match alone.
 */
export function englishHit(nameEn: string | null | undefined, line: string): { from: number; to: number; len: number } | null {
  const name = [...new Set(enWords(nameEn ?? '').map((x) => x.w).filter((w) => w.length >= 2))]
  if (!name.some(enDistinct)) return null
  const words = enWords(line)
  const all = enWords(nameEn ?? '').map((x) => x.w)
  for (let i = 0; i + all.length <= words.length; i++) {
    if (all.every((w, k) => words[i + k]?.w === w)) {
      return { from: words[i]!.at, to: words[i + all.length - 1]!.end, len: 50 + all.length }
    }
  }
  const hit = words.filter((x) => name.includes(x.w))
  const kinds = new Set(hit.map((x) => x.w))
  const strong = [...kinds].filter(enDistinct)
  if (!strong.length || (kinds.size < 2 && !strong.some((w) => w.length >= 5))) return null
  return { from: Math.min(...hit.map((h) => h.at)), to: Math.max(...hit.map((h) => h.end)), len: 10 * kinds.size }
}

/** The rule for showing "อ่านเมนูนี้": ≥ 2 menus named, or 1 with a quantity. */
export function looksLikeOrder(d: Detected): boolean {
  return d.mentions >= 2 || (d.mentions === 1 && d.withQty)
}

// ---------------------------------------------------------------- what one action reads

export interface ReadSource {
  convId: string
  kind: 'text' | 'image'
  /** Message ids sent to the API (oldest first). */
  ids: string[]
  /** Customer texts (for the image: the ones sent right after it). */
  texts: string[]
  imageUrl: string | null
  /** The bubble that shows the action. */
  anchorId: string
}

const isMsg = (x: ThreadItem): x is ChatMessage => x.kind === 'message'
const isCustomerText = (x: ChatMessage): boolean => x.direction === 'in' && x.type === 'text' && !!x.text?.trim()
const ms = (at: string): number => Date.parse(at) || 0

/**
 * The run of consecutive customer texts that ends at `id` (a staff/AI message, a picture or a 30 min gap ends a
 * run). null when `id` is not the last text of its run.
 */
export function textRunEndingAt(thread: readonly ThreadItem[], id: string): ChatMessage[] | null {
  const msgs = thread.filter(isMsg)
  const i = msgs.findIndex((m) => m.id === id)
  const cur = msgs[i]
  if (!cur || !isCustomerText(cur)) return null
  const next = msgs[i + 1]
  if (next && isCustomerText(next) && ms(next.at) - ms(cur.at) <= RUN_GAP_MS) return null
  const run: ChatMessage[] = [cur]
  for (let j = i - 1; j >= 0 && run.length < RUN_MAX; j--) {
    const m = msgs[j] as ChatMessage
    const later = run[0] as ChatMessage
    if (!isCustomerText(m) || ms(later.at) - ms(m.at) > RUN_GAP_MS) break
    run.unshift(m)
  }
  return run
}

/** Words that make a text right after a picture look like instructions about it. */
const ABOUT_PICTURE = /(ติ๊ก|ติก|ขีด|วง|เลือก|อย่างละ|เอา|ไม่เอา|กล่อง|แพ็ก|แพ็ค|ถุง|x\s*\d|\d)/i

export function textSource(thread: readonly ThreadItem[], item: ChatMessage, menus: PosMenu[]): ReadSource | null {
  const run = textRunEndingAt(thread, item.id)
  if (!run) return null
  const texts = run.map((m) => m.text ?? '')
  if (!looksLikeOrder(detectMenus(texts.join('\n'), menus))) return null
  return { convId: item.conversation_id, kind: 'text', ids: run.map((m) => m.id), texts, imageUrl: null, anchorId: item.id }
}

export function imageSource(thread: readonly ThreadItem[], item: ChatMessage): ReadSource {
  const msgs = thread.filter(isMsg)
  const i = msgs.findIndex((m) => m.id === item.id)
  const after: ChatMessage[] = []
  for (let j = i + 1; i >= 0 && j < msgs.length && after.length < AFTER_IMAGE_MAX; j++) {
    const m = msgs[j] as ChatMessage
    if (!isCustomerText(m) || ms(m.at) - ms(item.at) > AFTER_IMAGE_MS) break
    const t = m.text ?? ''
    if (t.length <= 160 && ABOUT_PICTURE.test(t)) after.push(m)
  }
  const url = item.attachments.find((a) => a.type === 'image' && a.url)?.url ?? null
  return {
    convId: item.conversation_id,
    kind: 'image',
    ids: [item.id, ...after.map((m) => m.id)],
    texts: after.map((m) => m.text ?? ''),
    imageUrl: url,
    anchorId: item.id,
  }
}

// ---------------------------------------------------------------- the recheck list

export interface RcRow {
  menuId: number
  p: string | null
  name: string
  qty: number
  /** The AI saw no number (yellow until the staff touch it). */
  guess: boolean
  /** Customer order (AI order); rows added by hand go last. */
  ord: number
  box: [number, number, number, number] | null
  snack: boolean
  /** Boxes "สุ่มเมนูที่เหลือ" put in (0/absent = the customer's or the staff's own); touching the row clears it. */
  rnd?: number
}

export type SortMode = 'p' | 'cust'

export function rowsFrom(items: MenuReadItem[]): RcRow[] {
  return items.map((it, i) => ({
    menuId: it.menu_id,
    p: it.p,
    name: it.name,
    qty: clampQty(it.qty),
    guess: !it.qty_explicit,
    ord: it.order ?? i,
    box: it.box && it.box.length === 4 ? it.box : null,
    snack: !!it.snack,
  }))
}

export function clampQty(v: unknown): number {
  const n = Math.floor(Number(v))
  if (!Number.isFinite(n) || n < 1) return 1
  return Math.min(MAX_QTY, n)
}

export function sortRows(rows: RcRow[], mode: SortMode): RcRow[] {
  const out = [...rows]
  if (mode === 'cust') out.sort((a, b) => a.ord - b.ord)
  else out.sort((a, b) => pNum(a.name) - pNum(b.name) || a.ord - b.ord)
  return out
}

export function setRowQty(rows: RcRow[], menuId: number, qty: unknown): RcRow[] {
  return rows.map((r) => (r.menuId === menuId ? { ...r, qty: clampQty(qty), guess: false, rnd: 0 } : r))
}

export function removeRow(rows: RcRow[], menuId: number): RcRow[] {
  return rows.filter((r) => r.menuId !== menuId)
}

/** Add from the shop's list: an existing row +1, a new one goes last (in customer order too). */
export function addRow(rows: RcRow[], m: PosMenu, mode: SortMode): RcRow[] {
  if (rows.some((r) => r.menuId === m.id)) {
    return rows.map((r) => (r.menuId === m.id ? { ...r, qty: clampQty(r.qty + 1), guess: false, rnd: 0 } : r))
  }
  const ord = Math.max(99999, ...rows.map((r) => r.ord)) + 1
  const row: RcRow = { menuId: m.id, p: pCodeOf(m.name), name: m.name, qty: 1, guess: false, ord, box: null, snack: m.type === 'snacks' }
  return sortRows([...rows, row], mode)
}

export interface Tally {
  menus: number
  meals: number
  snacks: number
}

export function tally(rows: RcRow[]): Tally {
  let meals = 0
  let snacks = 0
  for (const r of rows) {
    if (r.snack) snacks += r.qty
    else meals += r.qty
  }
  return { menus: rows.length, meals, snacks }
}

/** "N เมนู · M กล่อง — ยังขาด/เกิน X กล่อง จากแพ็ก Y" (same words as the API summary). */
export function packLine(t: Tally, limit: number | null): { level: 'ok' | 'warn' | 'none'; text: string } {
  let text = `${t.menus} เมนู · ${t.meals} กล่อง${t.snacks ? ` + ของว่าง ${t.snacks}` : ''}`
  if (!limit || limit <= 0) return { level: 'none', text }
  const diff = limit - t.meals
  if (diff === 0) return { level: 'ok', text: `${text} — ครบแพ็ก ${limit} พอดี` }
  text += diff > 0 ? ` — ยังขาด ${diff} กล่อง จากแพ็ก ${limit}` : ` — เกิน ${-diff} กล่อง จากแพ็ก ${limit}`
  return { level: 'warn', text }
}

export function confirmLabel(t: Tally): string {
  return `ยืนยันลงออเดอร์ ${t.menus} เมนู · ${t.meals} กล่อง`
}

export function doneToast(t: Tally): string {
  return `ลงเมนูจากแชทแล้ว ${t.menus} เมนู · ${t.meals} กล่อง`
}

/** The confirmed list as the order form's items (display order = pick order). */
export function toCartItems(rows: RcRow[]): PosCartItem[] {
  return rows.map((r) => ({ menu_id: r.menuId, qty: r.qty }))
}

/** "เพิ่มเมนู": a P code finds its dish first, then names (and the POS keyword) that contain the words. */
export function searchMenus(menus: PosMenu[], q: string, limit = 8): PosMenu[] {
  const t = norm(q)
  if (!t) return []
  const code = /^([a-z]{1,3})(\d{1,3})$/i.exec(t)
  const byCode = code ? menus.filter((m) => pCodeOf(m.name) === `${code[1]}${code[2]}`.toUpperCase()) : []
  const en = t.length >= 3 && /^[a-z]+$/.test(t)
  const rest = menus.filter(
    (m) => !byCode.includes(m) && (norm(m.name).includes(t) || norm(m.code ?? '') === t || (en && norm(m.name_en ?? '').includes(t))),
  )
  return [...byCode, ...rest.sort((a, b) => pNum(a.name) - pNum(b.name))].slice(0, limit)
}

/** The line of the customer's text a row came from (POS aiaLineFor): its code first, then its name. */
export function lineFor(row: Pick<RcRow, 'name' | 'p'>, lines: string[], nameEn?: string | null): number {
  if (row.p) {
    const re = new RegExp(`(?<![a-z0-9])${row.p.replace(/(\d+)$/, '\\s?$1')}(?![0-9])`, 'i')
    const i = lines.findIndex((l) => re.test(l))
    if (i >= 0) return i
  }
  const body = norm(dishName(row.name))
  const w = Math.min(WINDOW, body.length)
  for (let k = 0; body.length >= 4 && k + w <= body.length; k++) {
    const piece = body.slice(k, k + w)
    const i = lines.findIndex((l) => norm(l).includes(piece))
    if (i >= 0) return i
  }
  return nameEn ? lines.findIndex((l) => englishHit(nameEn, l) !== null) : -1
}

/** A model box [ymin,xmin,ymax,xmax] (0–1000) as CSS percentages over the picture. */
export function boxStyle(box: [number, number, number, number]): Record<string, string> {
  const [y0, x0, y1, x1] = box
  return { top: `${y0 / 10}%`, left: `${x0 / 10}%`, height: `${(y1 - y0) / 10}%`, width: `${(x1 - x0) / 10}%` }
}

// ---------------------------------------------------------------- pack (mock mode mirrors api/app/ai/menu_read.py)

const PACK_GENERIC = new Set(['มื้อ', 'แพ็ก', 'กล่อง', 'ลด', 'บาท', 'meal', 'meals', 'pack', 'day', 'วัน'])
function packNorm(s: string): string {
  let v = (s ?? '').toLowerCase().replace(/\d+(?:\.\d+)?\s*%/g, ' ')
  for (const [a, b] of [
    ['days', 'วัน'],
    ['day', 'วัน'],
    ['แพ็ค', 'แพ็ก'],
    ['แพค', 'แพ็ก'],
    ['meals', 'มื้อ'],
    ['meal', 'มื้อ'],
  ] as const)
    v = v.split(a).join(b)
  return v
}

export function packScore(name: string, text: string, boxes: number | null): number {
  const t = packNorm(text).replace(/\s+/g, '')
  let score = 0
  for (const tok of packNorm(name).split(/[\s/().,:+-]+/)) {
    if (!tok || PACK_GENERIC.has(tok) || (boxes !== null && tok === String(boxes))) continue
    if (/^\d$/.test(tok) && !new RegExp(`(?<!\\d)${tok}(?!\\d)`).test(t)) continue
    if (t.includes(tok)) score++
  }
  return score
}

export function suggestPack(sets: PosSetMenu[], boxes: number | null, text: string | null): MenuReadResult['pack'] {
  let n = boxes
  if (n === null && text) {
    const m = /(\d{1,3})\s*(?:กล่อง|มื้อ|แพ็ก|แพ็ค|meals?|box)/i.exec(text) ?? /(?:แพ็ก|แพ็ค|pack|แพค)\s*(\d{1,3})/i.exec(text)
    n = m ? Number(m[1]) : null
  }
  const stated = n !== null || text ? { boxes: n, text } : null
  const cands = n === null ? [] : sets.filter((s) => s.meals === n)
  let pick: PosSetMenu | null = cands.length === 1 ? (cands[0] ?? null) : null
  if (cands.length > 1 && text) {
    const scored = cands.map((s) => ({ s, v: packScore(s.name, text, n) })).sort((a, b) => b.v - a.v)
    if ((scored[0]?.v ?? 0) > 0 && (scored[0]?.v ?? 0) > (scored[1]?.v ?? 0)) pick = scored[0]?.s ?? null
  }
  const out = (s: PosSetMenu): MenuReadPack => ({ id: s.id, name: s.name, meals: s.meals, price: s.price })
  return { stated, suggested: pick ? out(pick) : null, candidates: cands.map(out) }
}

// ---------------------------------------------------------------- "สุ่มเมนูที่เหลือ" (POS sale.js #btn-random)

export const KIND_LABEL: Record<string, string> = {
  chicken: 'ไก่',
  pork: 'หมู',
  beef: 'เนื้อ',
  fish: 'ปลา',
  seafood: 'ทะเล',
  pasta: 'เส้น',
  veg: 'ผัก',
  vegetable: 'ผัก',
}
export const KIND_ORDER = ['chicken', 'pork', 'beef', 'fish', 'seafood', 'pasta', 'veg', 'vegetable', 'other', 'snacks']
export function kindOf(m: Pick<PosMenu, 'type' | 'meat_type'>): string {
  if (m.type === 'snacks') return 'snacks'
  return m.meat_type && KIND_LABEL[m.meat_type] ? m.meat_type : 'other'
}
export function kindLabel(k: string): string {
  return k === 'snacks' ? 'ของว่าง' : k === 'other' ? 'อื่นๆ' : (KIND_LABEL[k] ?? k)
}
/** Kinds present in a list of menus with how many dishes each (chip order). */
export function kindCounts(menus: readonly PosMenu[]): { k: string; label: string; n: number }[] {
  const n = new Map<string, number>()
  for (const m of menus) n.set(kindOf(m), (n.get(kindOf(m)) ?? 0) + 1)
  return KIND_ORDER.filter((k) => n.has(k)).map((k) => ({ k, label: kindLabel(k), n: n.get(k) ?? 0 }))
}

/**
 * The menus a random fill may use — POS randomProduct() + inc/product_list.php: on sale (the catalog), no snacks, no
 * premium dishes (`plus` > 0) unless `withPlus` (chip "รวมเมนูบวกเงิน", off by default — owner 28 ก.ย. 69); an
 * "อาหารเจ" pack only J… menus, a keto pack only KT… menus.
 */
export function randomPool(menus: readonly PosMenu[], packName: string | null | undefined, withPlus = false): PosMenu[] {
  const base = menus.filter((m) => m.type !== 'snacks' && (withPlus || !(Number(m.plus) > 0)))
  const n = (packName ?? '').toLowerCase()
  if (n.includes('อาหารเจ')) return base.filter((m) => /^J/i.test(m.name))
  if (n.includes('keto')) return base.filter((m) => /^KT/i.test(m.name))
  return base
}

/** The kinds the fill may use from the read's hint: null = every kind · [] = none fits what the customer said. */
export function allowFromHint(hint: MixHint | null | undefined, kinds: readonly string[]): string[] | null {
  if (hint?.only.length) return kinds.filter((k) => hint.only.includes(k))
  if (hint?.exclude.length) return kinds.filter((k) => !hint.exclude.includes(k))
  return null
}

function shuffled<T>(xs: readonly T[], rng: () => number): T[] {
  const a = [...xs]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    ;[a[i], a[j]] = [a[j] as T, a[i] as T]
  }
  return a
}

/**
 * `need` boxes over the pool (limited to the `allow`ed kinds), one at a time to the dish with the fewest boxes so
 * far (what is already in the list counts): dishes not in the list come first (the POS picks each dish once before
 * it repeats) and the boxes end up spread evenly; ties go to a random order. With enough new dishes nothing already
 * in the list gets more. Returns what to add, in the order it was first picked.
 */
export function randomFill(
  picked: readonly { menuId: number; qty: number }[],
  pool: readonly PosMenu[],
  need: number,
  allow: readonly string[] | null,
  rng: () => number = Math.random,
): { menuId: number; qty: number }[] {
  const n = Math.min(MAX_QTY * 5, Math.floor(need))
  if (!(n > 0)) return []
  const have = new Map<number, number>()
  for (const p of picked) if (p.qty > 0) have.set(p.menuId, (have.get(p.menuId) ?? 0) + p.qty)
  // random order, then the dishes not in the list first (stable): the first of the lowest wins each box
  const use = shuffled(
    pool.filter((m) => allow === null || allow.includes(kindOf(m))),
    rng,
  ).sort((a, b) => Number(have.has(a.id)) - Number(have.has(b.id)))
  if (!use.length) return []
  const level = use.map((m) => have.get(m.id) ?? 0)
  const add = new Map<number, number>()
  for (let i = 0; i < n; i++) {
    let k = 0
    for (let j = 1; j < use.length; j++) if ((level[j] as number) < (level[k] as number)) k = j
    level[k] = (level[k] as number) + 1
    const id = (use[k] as PosMenu).id
    add.set(id, (add.get(id) ?? 0) + 1)
  }
  return [...add].map(([menuId, qty]) => ({ menuId, qty }))
}

/** The fill into the recheck list: new rows go last (marked `rnd`), an existing row gets more (its `rnd` grows). */
export function applyFill(rows: RcRow[], adds: { menuId: number; qty: number }[], menus: readonly PosMenu[], mode: SortMode): RcRow[] {
  let out = [...rows]
  let ord = Math.max(99999, ...rows.map((r) => r.ord))
  for (const a of adds) {
    const i = out.findIndex((r) => r.menuId === a.menuId)
    if (i >= 0) {
      const r = out[i] as RcRow
      out[i] = { ...r, qty: clampQty(r.qty + a.qty), rnd: (r.rnd ?? 0) + a.qty }
      continue
    }
    const m = menus.find((x) => x.id === a.menuId)
    if (!m) continue
    out = [...out, { menuId: m.id, p: pCodeOf(m.name), name: m.name, qty: clampQty(a.qty), guess: false, ord: ++ord, box: null, snack: false, rnd: a.qty }]
  }
  return sortRows(out, mode)
}

/** Takes the untouched random boxes out again ("สุ่มใหม่"). */
export function unfill(rows: RcRow[]): RcRow[] {
  return rows.map((r) => (r.rnd ? { ...r, qty: r.qty - r.rnd, rnd: 0 } : r)).filter((r) => r.qty > 0)
}

// ---------------------------------------------------------------- "คละ" hint (mock mode mirrors api/app/ai/menu_read.py mix_hint)

const MIX_TYPE_WORDS: [string, string][] = [
  ['อาหารทะเล', 'seafood'], ['ปลาหมึก', 'seafood'], ['ซีฟู้ด', 'seafood'], ['ทะเล', 'seafood'], ['กุ้ง', 'seafood'],
  ['หมึก', 'seafood'], ['เนื้อปลา', 'fish'], ['เนื้อไก่', 'chicken'], ['เนื้อหมู', 'pork'], ['เนื้อวัว', 'beef'],
  ['ไก่', 'chicken'], ['หมู', 'pork'], ['เนื้อ', 'beef'], ['วัว', 'beef'], ['ปลา', 'fish'], ['พาสต้า', 'pasta'],
  ['เส้น', 'pasta'], ['seafood', 'seafood'], ['chicken', 'chicken'], ['pork', 'pork'], ['beef', 'beef'],
  ['fish', 'fish'], ['pasta', 'pasta'],
]
const MIX_T = `(?:${MIX_TYPE_WORDS.map(([w]) => w).join('|')})`
const MIX_L = `${MIX_T}(?:\\s*(?:กับ|และ|หรือ|,|/|\\+|&|and|or)?\\s*${MIX_T})*`
const MIX_EXCLUDE = `(?:ไม่เอา|ไม่ทาน|ไม่กิน|ไม่รับ|ไม่ใส่|งด|ยกเว้น|เว้น|\\bno|\\bwithout)\\s*(?:ทั้ง)?\\s*(${MIX_L})`
const MIX_ONLY = `(?:คละ\\s*(?:เอา)?\\s*(?:แต่|แค่|เฉพาะ)?|(?<!ไม่)เอาแต่|(?<!ไม่)เอาแค่|ขอแต่|ขอแค่|เฉพาะ|\\bonly|\\bjust)\\s*(${MIX_L})`
const MIX_ONLY_AFTER = `(${MIX_L})\\s*(?:ล้วน|อย่างเดียว|only)`

function mixTypes(chunk: string): string[] {
  const low = chunk.toLowerCase()
  const out: string[] = []
  for (let i = 0; i < low.length; ) {
    const hit = MIX_TYPE_WORDS.find(([w]) => low.startsWith(w, i))
    if (!hit) {
      i++
      continue
    }
    if (!out.includes(hit[1])) out.push(hit[1])
    i += hit[0].length
  }
  return out
}

/** {mix, only[], exclude[], said}: the meat types the customer allows for the boxes the shop picks. */
export function mixHint(text: string, mix: boolean): MixHint {
  const only: string[] = []
  const exclude: string[] = []
  const spans: [number, number, string][] = []
  const take = (m: RegExpMatchArray): boolean => {
    const a = m.index ?? 0
    const b = a + m[0].length
    if (spans.some(([x, y]) => a < y && x < b)) return false
    spans.push([a, b, m[0].trim()])
    return true
  }
  for (const m of (text ?? '').matchAll(new RegExp(MIX_EXCLUDE, 'gi'))) {
    if (take(m)) for (const k of mixTypes(m[1] ?? '')) if (!exclude.includes(k)) exclude.push(k)
  }
  const onlies = [...(text ?? '').matchAll(new RegExp(MIX_ONLY, 'gi')), ...(mix ? (text ?? '').matchAll(new RegExp(MIX_ONLY_AFTER, 'gi')) : [])]
  for (const m of onlies) {
    if (take(m)) for (const k of mixTypes(m[1] ?? '')) if (!only.includes(k) && !exclude.includes(k)) only.push(k)
  }
  const said: string[] = []
  for (const [, , x] of spans.sort((p, q) => p[0] - q[0])) if (!said.some((y) => y.includes(x))) said.push(x)
  return { mix, only, exclude, said: said.join(' · ').slice(0, 80) }
}
